import React, { forwardRef } from "react";
import {
  CircleCheck,
  Inbox,
  SearchX,
  TriangleAlert,
  type LucideIcon,
} from "lucide-react";
import { cn } from "../../lib/utils.js";
import { TONES, type StatusTone } from "../../lib/tones.js";
import { IconTile } from "../../atoms/IconTile/index.js";

/* ---------------- EmptyState ----------------
   Encodes the content-design empty-state anatomy (docs/CONTENT-DESIGN.md §3,
   strategy §17.6): three distinct cases, never conflated. `variant` names the
   case and drives the default icon + tone; the error variant is the inline
   form of a load failure (§17.7). Anatomy: icon → title → description →
   primary action → optional secondary action. A framed empty state is this
   part inside `<Card variant="dashed">`; it has no frame of its own. */

export type EmptyStateVariant = "first-use" | "no-results" | "error";

/** `sm` is a one-line row for a small region, `md` today's centred panel and
 *  `lg` the page-level panel. */
export type EmptyStateSize = "sm" | "md" | "lg";

/** The tone of a first-use or no-results state. `error` is always critical. */
export type EmptyStateTone = Extract<StatusTone, "neutral" | "healthy">;

/** The title's heading level. The page's h1 stays the host's. */
export type EmptyStateHeadingLevel = 2 | 3 | 4 | 5 | 6;

interface EmptyStateBaseProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "title"
> {
  /**
   * `md` (default): the centred panel with a 40px medallion. `lg`: the page
   * panel with a 48px medallion and a larger title. `sm`: a start-aligned
   * one-line row with no medallion; only `error`, `tone="healthy"` or an
   * explicit `icon` draws a 14px glyph, and the actions sit inline.
   */
  size?: EmptyStateSize;
  /** Override the default per-variant icon. */
  icon?: React.ReactNode;
  title: React.ReactNode;
  description?: React.ReactNode;
  /** Primary action (e.g. the first-use CTA, or Clear filters / Retry). */
  action?: React.ReactNode;
  /** Optional secondary action (e.g. "Learn more"). */
  secondaryAction?: React.ReactNode;
  /** Render the title as this heading. Default: a plain `div` (`span` at `sm`). */
  headingLevel?: EmptyStateHeadingLevel;
}

interface EmptyStateToneProps extends EmptyStateBaseProps {
  /** Which empty case this is (content-design §3). Drives the default icon + tone. */
  variant?: "first-use" | "no-results";
  /** `neutral` (default) or `healthy`, a success screen: green medallion and
   *  a CircleCheck default icon. */
  tone?: EmptyStateTone;
}

interface EmptyStateErrorProps extends EmptyStateBaseProps {
  variant: "error";
  /** An error is always critical and owns its icon, so it takes no tone. */
  tone?: never;
}

export type EmptyStateProps = EmptyStateToneProps | EmptyStateErrorProps;

const DEFAULT_ICON: Record<EmptyStateVariant, LucideIcon> = {
  "first-use": Inbox,
  "no-results": SearchX,
  error: TriangleAlert,
};

/* The centred panel per size. `md` is today's strings, unchanged; `lg` swaps
   only the medallion, padding, type steps and the default glyph. The
   description's `mx-auto` centres its max-w-xs box under a wider title: the
   text block shrink-wraps to its widest child, and a max-width block would
   otherwise sit at that block's left edge. */
const PANEL = {
  md: {
    root: "flex flex-col items-center text-center px-6 py-10 gap-3",
    title: "text-sm font-medium text-pb-text",
    description: "text-xs text-pb-text-secondary max-w-xs mx-auto",
    glyph: "w-5 h-5",
  },
  lg: {
    root: "flex flex-col items-center text-center px-6 py-16 gap-3",
    title: "text-lg font-medium text-pb-text",
    description: "text-sm text-pb-text-secondary max-w-xs mx-auto",
    glyph: "w-6 h-6",
  },
} as const;

export const EmptyState = forwardRef<HTMLDivElement, EmptyStateProps>(
  function EmptyState(
    {
      variant = "first-use",
      size = "md",
      tone,
      icon,
      title,
      description,
      action,
      secondaryAction,
      headingLevel,
      className,
      ...rest
    },
    ref,
  ) {
    const isError = variant === "error";
    const healthy = !isError && tone === "healthy";
    const Heading = headingLevel ? (`h${headingLevel}` as const) : undefined;
    const actions = (action || secondaryAction) && (
      <div
        className={
          size === "sm"
            ? "flex shrink-0 items-center gap-2"
            : "mt-1 flex items-center gap-2"
        }
      >
        {action}
        {secondaryAction}
      </div>
    );
    if (size === "sm") {
      /* The glyph is a second channel for a failure or a success, so colour
         is never the only one; a neutral row draws none unless asked. */
      const glyphTone: StatusTone | undefined = isError
        ? "critical"
        : healthy
          ? "healthy"
          : undefined;
      const Default = isError ? TriangleAlert : CircleCheck;
      const glyph = icon ?? (glyphTone ? <Default /> : null);
      const Title = Heading ?? "span";
      return (
        <div
          ref={ref}
          className={cn(
            "flex items-center gap-2 text-start text-dense-1 text-pb-text-muted",
            className,
          )}
          {...rest}
        >
          {glyph && (
            <span
              aria-hidden="true"
              className={cn(
                "flex shrink-0 [&_svg]:size-3.5",
                glyphTone && TONES[glyphTone].text,
              )}
            >
              {glyph}
            </span>
          )}
          <div className="min-w-0">
            <Title className={Heading && "inline"}>{title}</Title>
            {description && (
              <>
                <span aria-hidden="true"> · </span>
                <span>{description}</span>
              </>
            )}
          </div>
          {actions}
        </div>
      );
    }
    const panel = PANEL[size];
    const Glyph = healthy ? CircleCheck : DEFAULT_ICON[variant];
    const Title = Heading ?? "div";
    return (
      <div ref={ref} className={cn(panel.root, className)} {...rest}>
        <IconTile
          shape="circle"
          size={size}
          variant="soft"
          tone={isError ? "critical" : healthy ? "healthy" : "neutral"}
        >
          {icon ?? <Glyph className={panel.glyph} />}
        </IconTile>
        <div className="space-y-1">
          <Title className={panel.title}>{title}</Title>
          {description && (
            <div className={panel.description}>{description}</div>
          )}
        </div>
        {actions}
      </div>
    );
  },
);
